import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { AppRouteConstants, PathRouteConstants } from 'src/app/shared/constants/app-route.constants';
import { UtilityService } from 'src/app/shared/services/utility.service';
import { LessonCurriculumApiService } from '../../services/lesson-curriculum-api.service';
import { LessonsApiService } from '../../services/lessons-api.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { ApiDataService } from 'src/app/shared/services/api.service';

@Component({
  selector: 'app-lesson-curriculum-details',
  templateUrl: './lesson-curriculum-details.component.html',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
})
export class LessonCurriculumDetailsComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject<void>();
  curriculumId: number;
  curriculum: any;
  lessonPlans: any[] = [];
  allLessonPlans: any[] = [];
  filteredLessonPlans: any[] = [];
  lessonPlanSearchControl = new FormControl('');
  selectedLessonPlanId: number = null;
  loading = false;
  allCurriculumLessonPlans: any[] = [];
  allGrades: any[] = [];
  availableCurriculumGrades: any[] = [];
  selectedCurriculumGradeIds: number[] = [];
  duplicateLessonPlanError = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private navigationService: NavigationService,
    private lessonCurriculumApiService: LessonCurriculumApiService,
    private lessonsApiService: LessonsApiService,
    private apiService: ApiDataService,
    private toastrService: ToastrService,
  ) { }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.curriculumId = +params['id'];
      this.getCurriculum();
    });
    this.getGrades();
    this.getAllLessonPlans();
    this.lessonPlanSearchControl.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.filterLessonPlans());
  }

  getGrades() {
    this.apiService.getAllGrades()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.allGrades = res.sort((a, b) =>
            this.getGradeSortValue(a.grade_level) - this.getGradeSortValue(b.grade_level)
          );
          this.setAvailableCurriculumGrades();
        }
      });
  }

  getCurriculum() {
    this.loading = true;
    this.lessonCurriculumApiService.getCurriculum(this.curriculumId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.curriculum = res;
          this.allCurriculumLessonPlans = this.sortCurriculumLessonPlans(res.lessonPlans ?? []);
          this.setAvailableCurriculumGrades();
          this.filterCurriculumLessonPlans();
        }
        this.loading = false;
      });
  }

  getAllLessonPlans() {
    this.lessonsApiService.getLessonNames()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.allLessonPlans = this.sortLessonPlans(res);
          this.filterLessonPlans();
        }
      });
  }

  filterLessonPlans() {
    const searchTerm = (this.lessonPlanSearchControl.value || '').toLowerCase();

    this.filteredLessonPlans = this.allLessonPlans.filter(lp => {
      const matchesSearch = `${lp.lesson_plan_number || ''} ${lp.grade_id?.grade_level || ''} ${lp.lesson_plan_name || ''}`
        .toLowerCase()
        .includes(searchTerm);

      const matchesGrade =
        !this.selectedCurriculumGradeIds.length ||
        this.selectedCurriculumGradeIds.includes(lp.grade_id?.id);

      const isNotAlreadyAdded = !this.isLessonPlanAlreadyAdded(lp.id);
      
      /* 
      Drop box will show already added lessons, add && isNotAlreadyAdded
      after matchesGrade in order to hide already added lessons
      */
      return matchesSearch && matchesGrade;
    });
  }


  addLessonPlan() {
    if (!this.selectedLessonPlanId) return;

    if (this.isLessonPlanAlreadyAdded(this.selectedLessonPlanId)) {
      this.duplicateLessonPlanError = true;
      this.toastrService.error('Lesson plan is already added to this Yearly Plan');
      this.selectedLessonPlanId = null;
      this.filterLessonPlans();
      return;
    }

    this.lessonCurriculumApiService.addLessonPlanToCurriculum({
      lesson_plan_id: this.selectedLessonPlanId,
      curriculum_name: this.curriculum.curriculum_name,
    })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
  next: res => {
    if (res) {
      this.selectedLessonPlanId = null;
      this.getCurriculum();
      this.toastrService.success('Lesson plan added to Yearly Plan');
    } else {
      this.toastrService.error('Failed to add lesson plan');
    }
  },
  error: () => {
    this.toastrService.error(
      'Sorry, another lesson plan with same number already exists for the grade'
    );
  }
});
  }

  removeLessonPlan(id: number) {
  this.lessonCurriculumApiService.removeLessonPlanFromCurriculum(id)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: res => {
        if (res) {
          this.allCurriculumLessonPlans = this.allCurriculumLessonPlans.filter(
            lp => Number(lp.id) !== Number(id)
          );

          this.lessonPlans = this.lessonPlans.filter(
            lp => Number(lp.id) !== Number(id)
          );

          this.setAvailableCurriculumGrades();
          this.filterCurriculumLessonPlans();
          this.filterLessonPlans();

          this.toastrService.success('Lesson plan removed from Yearly Plan');
        } else {
          this.toastrService.error('Failed to remove lesson plan');
        }
      },
      error: error => {
        this.toastrService.error(
          error?.error?.message || 'Failed to remove lesson plan'
        );
      }
    });
}

  goToLessonPlan(id: number) {
    this.navigationService.navigateToPage([
      PathRouteConstants.LESSONS + UtilityService.replaceText(AppRouteConstants.LESSON_DETAILS, [{ text: ':id', data: id }]),
    ]);
  }

  createLessonPlan() {
    this.router.navigate(
      [PathRouteConstants.LESSONS + AppRouteConstants.LESSON_CREATE],
      { queryParams: { curriculum_name: this.curriculum.curriculum_name } }
    );
  }

  editLessonPlan(lessonPlanId: number) {
    if (!lessonPlanId) {
      return;
    }

    this.router.navigate([
      PathRouteConstants.LESSONS + UtilityService.replaceText(AppRouteConstants.LESSON_UPDATE, [{ text: ':id', data: lessonPlanId }])
    ], {
      queryParams: {
        returnUrl: this.router.url,
      },
    });
  }

  navigateBack() {
    this.navigationService.navigateToPageAsync([PathRouteConstants.LESSONS], {
      queryParams: { tab: 'curriculum' },
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  /* When getLessons calls the backend, it returns grade.id,
     for whatever reason, the backend sorts grades by:
     1: 2nd
     2: 3rd
     3: 4th
     4: 5th
     5: 1st

     These bottom sort functions serve to locally correct this id mismatch
  */
  sortCurriculumLessonPlans(lessonPlans: any[]) {
    return [...lessonPlans].sort((a, b) => {
      const gradeA = this.getGradeSortValue(a?.lesson_plan_id?.grade_id?.grade_level);
      const gradeB = this.getGradeSortValue(b?.lesson_plan_id?.grade_id?.grade_level);
      const lessonNumberA = Number(a?.lesson_plan_id?.lesson_plan_number ?? 0);
      const lessonNumberB = Number(b?.lesson_plan_id?.lesson_plan_number ?? 0);

      if (gradeA !== gradeB) return gradeA - gradeB;
      return lessonNumberA - lessonNumberB;
    });
  }
  sortLessonPlans(lessonPlans: any[]) {
    return [...lessonPlans].sort((a, b) => {
      const gradeA = this.getGradeSortValue(a?.grade_id?.grade_level);
      const gradeB = this.getGradeSortValue(b?.grade_id?.grade_level);
      const lessonNumberA = Number(a?.lesson_plan_number ?? 0);
      const lessonNumberB = Number(b?.lesson_plan_number ?? 0);

      if (gradeA !== gradeB) return gradeA - gradeB;
      return lessonNumberA - lessonNumberB;
    });
  }

  getGradeSortValue(gradeLevel: string) {
    const gradeNumber = parseInt(gradeLevel, 10);
    return Number.isNaN(gradeNumber) ? Number.MAX_SAFE_INTEGER : gradeNumber;
  }

  setAvailableCurriculumGrades() {
    const hasFifthGrade = this.allCurriculumLessonPlans.some(lpc =>
      this.getGradeSortValue(lpc.lesson_plan_id?.grade_id?.grade_level) === 5
    );

    this.availableCurriculumGrades = this.allGrades.filter(grade => {
      const gradeNumber = this.getGradeSortValue(grade.grade_level);
      return gradeNumber >= 1 && gradeNumber <= 4 || (gradeNumber === 5 && hasFifthGrade);
    });

    const availableGradeIds = this.availableCurriculumGrades.map(grade => grade.id);
    this.selectedCurriculumGradeIds = this.selectedCurriculumGradeIds.filter(gradeId =>
      availableGradeIds.includes(gradeId)
    );
  }

  filterCurriculumLessonPlans() {
    if (!this.selectedCurriculumGradeIds.length) {
      this.lessonPlans = this.allCurriculumLessonPlans;
    } else {
      this.lessonPlans = this.allCurriculumLessonPlans.filter(lpc =>
        this.selectedCurriculumGradeIds.includes(lpc.lesson_plan_id?.grade_id?.id)
      );
    }

    this.filterLessonPlans();
  }

  //Prevents Duplicate Lesson plans to exist in curriculum
  isLessonPlanAlreadyAdded(lessonPlanId: number) {
    return this.allCurriculumLessonPlans.some(lpc =>
      lpc.lesson_plan_id?.id === lessonPlanId
    );
  }
}
